import React, { useState, useEffect, useMemo } from 'react';
import {
    Box,
    Typography,
    Paper,
    Tabs,
    Tab,
    Grid,
    Chip,
    useTheme,
    useMediaQuery,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
} from '@mui/material';
import ReactECharts from 'echarts-for-react';
import { useNavigate, useParams } from 'react-router-dom';
import EarningsTranscript from './EarningsTranscript';
import { formatNumber } from '../utils/numberUtils';

// ─── Helpers ────────────────────────────────────────────────────────────────

const formatEPS = (val) => {
    if (val == null) return 'N/A';
    return `$${parseFloat(val).toFixed(2)}`;
};

const formatRevenue = (val) => {
    if (val == null) return 'N/A';
    return `$${formatNumber(val)}`;
};

const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return 'N/A';
    return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
};

const getSurprisePct = (actual, estimated) => {
    if (actual == null || estimated == null || estimated === 0) return null;
    return ((actual - estimated) / Math.abs(estimated)) * 100;
};

const SurpriseChip = ({ actual, estimated }) => {
    const pct = getSurprisePct(actual, estimated);
    if (pct == null) return <Typography variant="body2" color="text.secondary">—</Typography>;
    const beat = pct >= 0;
    return (
        <Chip
            label={`${beat ? '+' : ''}${pct.toFixed(2)}%`}
            size="small"
            sx={{
                backgroundColor: beat ? 'rgba(14, 179, 91, 0.15)' : 'rgba(230, 35, 51, 0.15)',
                color: beat ? '#0eb35b' : '#e62333',
                fontWeight: 600,
                fontSize: '0.72rem',
                border: `1px solid ${beat ? 'rgba(14,179,91,0.3)' : 'rgba(230,35,51,0.3)'}`,
            }}
        />
    );
};

// ─── Next Earnings Card ──────────────────────────────────────────────────────

const NextEarningsCard = ({ entry, theme }) => {
    if (!entry) return null;
    const daysUntil = Math.ceil((new Date(entry.date) - new Date()) / (1000 * 60 * 60 * 24));
    if (daysUntil < 0) return null;

    return (
        <Paper
            elevation={0}
            sx={{
                p: 3,
                mb: 3,
                borderRadius: 2,
                border: `1px solid ${theme.palette.primary.main}44`,
                background: theme.palette.mode === 'dark'
                    ? 'linear-gradient(135deg, rgba(25,118,210,0.12) 0%, rgba(25,118,210,0.04) 100%)'
                    : 'linear-gradient(135deg, rgba(25,118,210,0.08) 0%, rgba(25,118,210,0.02) 100%)',
                position: 'relative',
                overflow: 'hidden',
                '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '4px',
                    height: '100%',
                    backgroundColor: theme.palette.primary.main,
                    borderRadius: '2px 0 0 2px',
                }
            }}
        >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <Typography variant="overline" sx={{ color: theme.palette.primary.main, fontWeight: 700, letterSpacing: 1.5 }}>
                    Next Earnings Report
                </Typography>
                <Chip
                    label={`In ${daysUntil} day${daysUntil !== 1 ? 's' : ''}`}
                    size="small"
                    sx={{ backgroundColor: theme.palette.primary.main, color: '#fff', fontWeight: 600, fontSize: '0.7rem' }}
                />
            </Box>
            <Grid container spacing={3}>
                <Grid item xs={12} sm={4}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>Report Date</Typography>
                    <Typography variant="h6" fontWeight={700}>{formatDate(entry.date)}</Typography>
                </Grid>
                <Grid item xs={6} sm={4}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>EPS Estimate</Typography>
                    <Typography variant="h6" fontWeight={700}>{formatEPS(entry.epsEstimated)}</Typography>
                </Grid>
                <Grid item xs={6} sm={4}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>Revenue Estimate</Typography>
                    <Typography variant="h6" fontWeight={700}>{formatRevenue(entry.revenueEstimated)}</Typography>
                </Grid>
            </Grid>
        </Paper>
    );
};

// ─── Beat/Miss Chart ─────────────────────────────────────────────────────────

const BeatMissChart = ({ data, theme }) => {
    const [metric, setMetric] = useState('eps');
    const [range, setRange] = useState('5Y');
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const filtered = useMemo(() => {
        const historical = data.filter(d => d.epsActual != null);
        const now = new Date();
        const cutoff = {
            '3Y': new Date(now.getFullYear() - 3, now.getMonth(), now.getDate()),
            '5Y': new Date(now.getFullYear() - 5, now.getMonth(), now.getDate()),
            'MAX': new Date(0),
        }[range];
        return historical
            .filter(d => new Date(d.date) >= cutoff)
            .sort((a, b) => new Date(a.date) - new Date(b.date));
    }, [data, range]);

    const chartOption = useMemo(() => {
        const isEPS = metric === 'eps';
        const actualKey = isEPS ? 'epsActual' : 'revenueActual';
        const estimatedKey = isEPS ? 'epsEstimated' : 'revenueEstimated';
        const labels = filtered.map(d => formatDate(d.date));
        const actuals = filtered.map(d => d[actualKey]);
        const estimates = filtered.map(d => d[estimatedKey]);
        const barColors = filtered.map(d => {
            const actual = d[actualKey];
            const estimated = d[estimatedKey];
            if (actual == null) return 'rgba(150,150,150,0.5)';
            return actual >= estimated ? 'rgba(14,179,91,0.85)' : 'rgba(230,35,51,0.85)';
        });
        const textColor = theme.palette.text.primary;
        const gridColor = theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)';

        return {
            backgroundColor: 'transparent',
            tooltip: {
                trigger: 'axis',
                axisPointer: { type: 'shadow' },
                backgroundColor: theme.palette.mode === 'dark' ? 'rgba(15,20,30,0.95)' : 'rgba(255,255,255,0.97)',
                borderColor: theme.palette.divider,
                borderWidth: 1,
                textStyle: { color: textColor },
                formatter: (params) => {
                    const idx = params[0].dataIndex;
                    const d = filtered[idx];
                    const actual = d[actualKey];
                    const estimated = d[estimatedKey];
                    const pct = getSurprisePct(actual, estimated);
                    const beat = pct != null && pct >= 0;
                    return `
                        <div style="padding:4px 0">
                            <div style="font-weight:700;margin-bottom:6px">${formatDate(d.date)}</div>
                            <div>Actual: <b>${isEPS ? formatEPS(actual) : formatRevenue(actual)}</b></div>
                            <div>Estimate: <b>${isEPS ? formatEPS(estimated) : formatRevenue(estimated)}</b></div>
                            ${pct != null ? `<div style="color:${beat ? '#0eb35b' : '#e62333'};font-weight:600;margin-top:4px">
                                ${beat ? '▲' : '▼'} ${Math.abs(pct).toFixed(2)}% ${beat ? 'Beat' : 'Miss'}
                            </div>` : ''}
                        </div>
                    `;
                }
            },
            grid: { left: '3%', right: '3%', bottom: '3%', top: '8%', containLabel: true },
            xAxis: {
                type: 'category',
                data: labels,
                axisLabel: { color: textColor, fontSize: isMobile ? 9 : 11, rotate: isMobile ? 45 : 0 },
                axisLine: { lineStyle: { color: gridColor } },
                splitLine: { show: false },
            },
            yAxis: {
                type: 'value',
                axisLabel: {
                    color: textColor,
                    fontSize: 11,
                    formatter: (val) => isEPS ? `$${val.toFixed(2)}` : `$${formatNumber(val)}`,
                },
                splitLine: { lineStyle: { color: gridColor } },
            },
            series: [
                {
                    name: 'Estimate',
                    type: 'line',
                    data: estimates,
                    lineStyle: { color: theme.palette.primary.main, width: 2, type: 'dashed' },
                    itemStyle: { color: theme.palette.primary.main },
                    symbol: 'circle',
                    symbolSize: 5,
                    z: 3,
                },
                {
                    name: 'Actual',
                    type: 'bar',
                    data: actuals,
                    itemStyle: {
                        color: (params) => barColors[params.dataIndex],
                        borderRadius: [4, 4, 0, 0],
                    },
                    barMaxWidth: 40,
                    z: 2,
                },
            ],
        };
    }, [filtered, metric, theme, isMobile]);

    return (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
                <Box sx={{ display: 'flex', gap: 1 }}>
                    {['eps', 'revenue'].map(m => (
                        <Chip
                            key={m}
                            label={m === 'eps' ? 'EPS' : 'Revenue'}
                            onClick={() => setMetric(m)}
                            sx={{
                                cursor: 'pointer',
                                fontWeight: 600,
                                backgroundColor: metric === m ? theme.palette.primary.main : theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)',
                                color: metric === m ? '#fff' : theme.palette.text.primary,
                            }}
                        />
                    ))}
                </Box>
                <Box sx={{ display: 'flex', gap: 1 }}>
                    {['3Y', '5Y', 'MAX'].map(r => (
                        <Chip
                            key={r}
                            label={r}
                            onClick={() => setRange(r)}
                            sx={{
                                cursor: 'pointer',
                                fontWeight: 600,
                                backgroundColor: range === r ? theme.palette.primary.main : theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)',
                                color: range === r ? '#fff' : theme.palette.text.primary,
                            }}
                        />
                    ))}
                </Box>
            </Box>
            <Box sx={{ display: 'flex', gap: 2, mb: 1.5, flexWrap: 'wrap' }}>
                {[
                    { color: '#0eb35b', label: 'Beat', dashed: false },
                    { color: '#e62333', label: 'Miss', dashed: false },
                    { color: theme.palette.primary.main, label: 'Estimate', dashed: true },
                ].map(({ color, label, dashed }) => (
                    <Box key={label} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Box sx={{
                            width: dashed ? 16 : 10,
                            height: dashed ? 0 : 10,
                            backgroundColor: dashed ? 'transparent' : color,
                            borderRadius: dashed ? 0 : 2,
                            borderTop: dashed ? `2px dashed ${color}` : 'none',
                        }} />
                        <Typography variant="caption" color="text.secondary">{label}</Typography>
                    </Box>
                ))}
            </Box>
            <ReactECharts option={chartOption} style={{ height: 320 }} notMerge={true} />
        </Box>
    );
};

// ─── Earnings History Table ───────────────────────────────────────────────────

const EarningsTable = ({ data, theme }) => {
    const historical = data
        .filter(d => d.epsActual != null)
        .sort((a, b) => new Date(b.date) - new Date(a.date));

    if (!historical.length) return null;

    return (
        <TableContainer component={Paper} elevation={0} sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: 2, mt: 3 }}>
            <Table size="small">
                <TableHead>
                    <TableRow>
                        {['Date', 'EPS Est.', 'EPS Actual', 'EPS Surprise', 'Rev. Est.', 'Rev. Actual', 'Rev. Surprise'].map(h => (
                            <TableCell key={h} align={h === 'Date' ? 'left' : 'right'} sx={{
                                fontWeight: 700, fontSize: '0.75rem', color: theme.palette.text.secondary,
                                textTransform: 'uppercase', letterSpacing: 0.5,
                                borderBottom: `1px solid ${theme.palette.divider}`, py: 1.5,
                            }}>
                                {h}
                            </TableCell>
                        ))}
                    </TableRow>
                </TableHead>
                <TableBody>
                    {historical.map((row, i) => (
                        <TableRow key={i} sx={{ '&:hover': { backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)' } }}>
                            <TableCell sx={{ fontSize: '0.85rem', py: 1.5, fontWeight: 600, borderBottom: `1px solid ${theme.palette.divider}22` }}>{formatDate(row.date)}</TableCell>
                            <TableCell sx={{ fontSize: '0.85rem', py: 1.5, borderBottom: `1px solid ${theme.palette.divider}22` }} align="right">{formatEPS(row.epsEstimated)}</TableCell>
                            <TableCell sx={{ fontSize: '0.85rem', py: 1.5, borderBottom: `1px solid ${theme.palette.divider}22` }} align="right">{formatEPS(row.epsActual)}</TableCell>
                            <TableCell sx={{ fontSize: '0.85rem', py: 1.5, borderBottom: `1px solid ${theme.palette.divider}22` }} align="right"><SurpriseChip actual={row.epsActual} estimated={row.epsEstimated} /></TableCell>
                            <TableCell sx={{ fontSize: '0.85rem', py: 1.5, borderBottom: `1px solid ${theme.palette.divider}22` }} align="right">{formatRevenue(row.revenueEstimated)}</TableCell>
                            <TableCell sx={{ fontSize: '0.85rem', py: 1.5, borderBottom: `1px solid ${theme.palette.divider}22` }} align="right">{formatRevenue(row.revenueActual)}</TableCell>
                            <TableCell sx={{ fontSize: '0.85rem', py: 1.5, borderBottom: `1px solid ${theme.palette.divider}22` }} align="right"><SurpriseChip actual={row.revenueActual} estimated={row.revenueEstimated} /></TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
    );
};

// ─── Owner Earnings Tab ───────────────────────────────────────────────────────

const OwnerEarningsTab = ({ data, theme }) => {
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const sorted = useMemo(() => [...data].sort((a, b) => new Date(a.date) - new Date(b.date)), [data]);
    const textColor = theme.palette.text.primary;
    const gridColor = theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)';

    const chartOption = {
        backgroundColor: 'transparent',
        tooltip: {
            trigger: 'axis',
            backgroundColor: theme.palette.mode === 'dark' ? 'rgba(15,20,30,0.95)' : 'rgba(255,255,255,0.97)',
            borderColor: theme.palette.divider,
            borderWidth: 1,
            textStyle: { color: textColor },
            formatter: (params) => {
                const d = sorted[params[0].dataIndex];
                return `
                    <div style="padding:4px 0">
                        <div style="font-weight:700;margin-bottom:6px">${d.period} ${d.fiscalYear}</div>
                        <div>Owner Earnings: <b>${d.ownersEarnings < 0 ? `-$${formatNumber(Math.abs(d.ownersEarnings))}` : `$${formatNumber(d.ownersEarnings)}`}</b></div>
                        <div>Per Share: <b>${d.ownersEarningsPerShare < 0 ? `-$${formatNumber(Math.abs(d.ownersEarningsPerShare))}` : `$${formatNumber(d.ownersEarningsPerShare)}`}</b></div>
                        <div>Maintenance CapEx: <b>$${formatNumber(Math.abs(d.maintenanceCapex))}</b></div>
                        <div>Growth CapEx: <b>$${formatNumber(Math.abs(d.growthCapex))}</b></div>
                    </div>
                `;
            }
        },
        grid: { left: '3%', right: '3%', bottom: '3%', top: '8%', containLabel: true },
        xAxis: {
            type: 'category',
            data: sorted.map(d => `${d.period} ${d.fiscalYear}`),
            axisLabel: { color: textColor, fontSize: isMobile ? 9 : 11, rotate: isMobile ? 45 : 0 },
            axisLine: { lineStyle: { color: gridColor } },
            splitLine: { show: false },
        },
        yAxis: {
            type: 'value',
            axisLabel: {
                color: textColor,
                fontSize: 11,
                formatter: (val) => val < 0 ? `-$${formatNumber(Math.abs(val))}` : `$${formatNumber(val)}`,
            },
            splitLine: { lineStyle: { color: gridColor } },
        },
        series: [
            {
                name: 'Owner Earnings',
                type: 'bar',
                data: sorted.map(d => d.ownersEarnings),
                itemStyle: {
                    color: { type: 'linear', x: 0, y: 0, x2: 0, y2: 1, colorStops: [{ offset: 0, color: 'rgba(25,118,210,0.9)' }, { offset: 1, color: 'rgba(25,118,210,0.3)' }] },
                    borderRadius: [4, 4, 0, 0],
                },
                barMaxWidth: 40,
            },
            {
                name: 'Per Share',
                type: 'line',
                data: sorted.map(d => d.ownersEarningsPerShare),
                lineStyle: { color: '#0eb35b', width: 2 },
                itemStyle: { color: '#0eb35b' },
                symbol: 'circle',
                symbolSize: 5,
            },
        ],
    };

    return (
        <Box>
            <Box sx={{ mb: 3 }}>
                <Typography variant="h6" fontWeight={700} gutterBottom>Owner Earnings</Typography>
                <Typography variant="body2" color="text.secondary">
                    A Buffett-style measure of true shareholder cash flow — net income adjusted for depreciation and capital expenditures.
                </Typography>
            </Box>

            <Paper elevation={0} sx={{ p: 3, borderRadius: 2, border: `1px solid ${theme.palette.divider}`, mb: 3 }}>
                <ReactECharts option={chartOption} style={{ height: 300 }} notMerge={true} />
            </Paper>

            <TableContainer component={Paper} elevation={0} sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: 2 }}>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            {['Period', 'Owner Earnings', 'Per Share', 'Maint. CapEx', 'Growth CapEx'].map(h => (
                                <TableCell key={h} align={h === 'Period' ? 'left' : 'right'} sx={{
                                    fontWeight: 700, fontSize: '0.75rem', color: theme.palette.text.secondary,
                                    textTransform: 'uppercase', letterSpacing: 0.5,
                                    borderBottom: `1px solid ${theme.palette.divider}`, py: 1.5,
                                }}>
                                    {h}
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {[...sorted].reverse().map((row, i) => (
                            <TableRow key={i} sx={{ '&:hover': { backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)' } }}>
                                <TableCell sx={{ fontSize: '0.85rem', py: 1.5, fontWeight: 600, borderBottom: `1px solid ${theme.palette.divider}22` }}>{row.period} {row.fiscalYear}</TableCell>
                                <TableCell sx={{ fontSize: '0.85rem', py: 1.5, borderBottom: `1px solid ${theme.palette.divider}22` }} align="right">{row.ownersEarnings < 0 ? `-$${formatNumber(Math.abs(row.ownersEarnings))}` : `$${formatNumber(row.ownersEarnings)}`}</TableCell>
                                <TableCell sx={{ fontSize: '0.85rem', py: 1.5, borderBottom: `1px solid ${theme.palette.divider}22` }} align="right">{formatEPS(row.ownersEarningsPerShare)}</TableCell>
                                <TableCell sx={{ fontSize: '0.85rem', py: 1.5, borderBottom: `1px solid ${theme.palette.divider}22` }} align="right">${formatNumber(Math.abs(row.maintenanceCapex))}</TableCell>
                                <TableCell sx={{ fontSize: '0.85rem', py: 1.5, borderBottom: `1px solid ${theme.palette.divider}22` }} align="right">${formatNumber(Math.abs(row.growthCapex))}</TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );
};

// ─── Reports Tab ─────────────────────────────────────────────────────────────

const ReportsTab = ({ data, theme }) => {
    const nextEarnings = useMemo(() => data.find(d => d.epsActual == null) ?? null, [data]);

    return (
        <Box>
            {nextEarnings && <NextEarningsCard entry={nextEarnings} theme={theme} />}
            <Paper elevation={0} sx={{ p: 3, borderRadius: 2, border: `1px solid ${theme.palette.divider}`, mb: 3 }}>
                <Typography variant="h6" fontWeight={700} gutterBottom>EPS & Revenue Beat / Miss</Typography>
                <BeatMissChart data={data} theme={theme} />
            </Paper>
            <EarningsTable data={data} theme={theme} />
        </Box>
    );
};

// ─── Main Component ───────────────────────────────────────────────────────────

const validSubtabs = ['reports', 'owner-earnings', 'transcript'];
const allLabels = ['Reports', 'Owner Earnings', 'Transcript'];

const Earnings = ({ data, subtab, transcriptData }) => {
    const theme = useTheme();
    const navigate = useNavigate();
    const { symbol } = useParams();

    const earningsReport = data?.earningsReport ?? [];
    const ownerEarnings = data?.ownerEarnings ?? [];

    const tabs = [
        { label: 'Reports', data: earningsReport, render: () => <ReportsTab data={earningsReport} theme={theme} /> },
        { label: 'Owner Earnings', data: ownerEarnings, render: () => <OwnerEarningsTab data={ownerEarnings} theme={theme} /> },
        { label: 'Transcript', data: transcriptData, render: () => <EarningsTranscript data={transcriptData} /> },
    ].filter(tab => tab.data && tab.data.length > 0);
    const [activeTab, setActiveTab] = useState(() => {
        const subtabIdx = validSubtabs.indexOf(subtab);
        if (subtabIdx < 0) return 0;
        const targetLabel = allLabels[subtabIdx];
        const idx = tabs.findIndex(t => t.label === targetLabel);
        return idx >= 0 ? idx : 0;
    });

    useEffect(() => {
        const subtabIdx = validSubtabs.indexOf(subtab);
        if (subtabIdx < 0) return;
        const targetLabel = allLabels[subtabIdx];
        const idx = tabs.findIndex(t => t.label === targetLabel);
        if (idx >= 0) setActiveTab(idx);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tabs.length]);

    if (tabs.length === 0) return null;

    const handleTabChange = (_, newVal) => {
        setActiveTab(newVal);
        const label = tabs[newVal].label.toLowerCase().replace(/\s+/g, '-');
        navigate(`/asset/${symbol}/earnings/${label}`, { replace: true });
    };

    return (
        <Box sx={{ width: '100%' }}>
            <Box sx={{ borderBottom: `1px solid ${theme.palette.divider}`, mb: 3 }}>
                <Tabs
                    value={activeTab}
                    onChange={handleTabChange}
                    sx={{
                        '& .MuiTab-root': { fontWeight: 600, fontSize: '0.875rem', textTransform: 'none', minWidth: 100, color: theme.palette.text.secondary },
                        '& .Mui-selected': { color: theme.palette.primary.main },
                        '& .MuiTabs-indicator': { backgroundColor: theme.palette.primary.main },
                    }}
                >
                    {tabs.map((tab, i) => <Tab key={i} label={tab.label} />)}
                </Tabs>
            </Box>
            {tabs[activeTab]?.render()}
        </Box>
    );
};

export default Earnings;